import { IoMdDownload } from '@react-icons/all-files/io/IoMdDownload'
import { useEffect, useMemo, useState } from 'react'
import { Diff } from '~/protos/agents'
import { BetterToast } from '~/utils/toast'
import { Header } from '../layout/ConnectionHeader'
import Typography from '../common/Typography'
import { DocumentViewer } from './DocumentViewer'
import { DownloadProgressButton } from '~/components/common/dropdowns/DownloadProgressButton'

export interface DocumentListProps {
  diffs: Diff[] | null
  className?: string
  title?: string
  description?: string
  emptyStateMessage?: string
  /** Callback when approve button is clicked (button shown when provided) */
  onApprove?: () => void
}

interface DocumentItem {
  name: string
  path: string
  content: string
  type: 'json' | 'md' | 'txt' | 'xml' | 'yaml' | 'csv'
  folder?: string
}

interface FolderGroup {
  folderName: string
  files: DocumentItem[]
}

function yieldToProgressPaint(): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, 0))
}

const getFileExtension = (filePath: string): DocumentItem['type'] => {
  const ext = filePath.split('.').pop()?.toLowerCase()
  switch (ext) {
    case 'json':
      return 'json'
    case 'md':
    case 'markdown':
      return 'md'
    case 'xml':
      return 'xml'
    case 'yml':
    case 'yaml':
      return 'yaml'
    case 'csv':
      return 'csv'
    default:
      return 'txt'
  }
}

const getFileName = (filePath: string): string => {
  return filePath.split('/').pop() || filePath
}

const getFolderName = (filePath: string): string => {
  const pathParts = filePath.split('/')
  if (pathParts.length <= 1) {
    return 'Root' // Files without folders go to "Root"
  }
  // Get the directory path (everything except the filename)
  return pathParts.slice(0, -1).join('/')
}

export function DocumentList({
  diffs,
  className = '',
  title = 'Generated Documents',
  emptyStateMessage = 'No documents generated',
  onApprove
}: DocumentListProps) {
  const [selectedDocumentIndex, setSelectedDocumentIndex] = useState<number | null>(null)
  const [isViewerOpen, setIsViewerOpen] = useState(false)
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  const [isDownloadingAll, setIsDownloadingAll] = useState(false)

  // Convert diffs to document items and group by folders (memoized for performance)
  const { documentItems, folderGroups } = useMemo(() => {
    if (!diffs || diffs.length === 0) {
      return { documentItems: [], folderGroups: [] }
    }

    // Convert diffs to document items, filtering out empty ones
    const items: DocumentItem[] = diffs
      .filter(diff => {
        const filePath = diff.bFilePath || diff.aFilePath
        const fileContent = diff.bFileContent || diff.aFileContent
        return filePath && fileContent && filePath.trim() !== '' && fileContent.trim() !== ''
      })
      .map(diff => {
        const fullPath = diff.bFilePath || diff.aFilePath || ''
        return {
          name: getFileName(fullPath),
          path: fullPath,
          content: diff.bFileContent || diff.aFileContent || '',
          type: getFileExtension(fullPath),
          folder: getFolderName(fullPath)
        }
      })
      .sort((a, b) => {
        // Priority order: postman_collection.json first, summary.md second, then alphabetical by folder then name
        if (a.name === 'postman_collection.json') return -1
        if (b.name === 'postman_collection.json') return 1
        if (a.name === 'summary.md') return -1
        if (b.name === 'summary.md') return 1

        // Sort by folder first, then by filename
        const folderCompare = a.folder!.localeCompare(b.folder!)
        if (folderCompare !== 0) return folderCompare
        return a.name.localeCompare(b.name)
      })

    // Group documents by folder
    const groups: FolderGroup[] = items.reduce((groupsAcc, item) => {
      const folderName = item.folder!
      let existingGroup = groupsAcc.find(group => group.folderName === folderName)

      if (!existingGroup) {
        existingGroup = { folderName, files: [] }
        groupsAcc.push(existingGroup)
      }

      existingGroup.files.push(item)
      return groupsAcc
    }, [] as FolderGroup[])

    // Sort folder groups: Root first, then alphabetically
    groups.sort((a, b) => {
      if (a.folderName === 'Root') return -1
      if (b.folderName === 'Root') return 1
      return a.folderName.localeCompare(b.folderName)
    })

    return { documentItems: items, folderGroups: groups }
  }, [diffs])

  // Update expanded folders when folderGroups change
  useEffect(() => {
    setExpandedFolders(new Set(folderGroups.map(group => group.folderName)))
  }, [folderGroups])

  if (!diffs || diffs.length === 0) {
    return (
      <div
        className={`w-full max-w-[800px] mx-auto px-12 ${className}`}
        style={{ padding: '0 max(48px, 3rem)' }}
      >
        <Header title={title} showBackButton={false} showSettingsButton={false} className='mb-6' />
        <div className='p-8 text-center'>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            fontWeight='medium'
            className='text-black/65'
          >
            {emptyStateMessage}
          </Typography>
        </div>
      </div>
    )
  }

  const toggleFolderExpansion = (folderName: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev)
      if (newSet.has(folderName)) {
        newSet.delete(folderName)
      } else {
        newSet.add(folderName)
      }
      return newSet
    })
  }

  const handleDocumentClick = (globalIndex: number) => {
    setSelectedDocumentIndex(globalIndex)
    setIsViewerOpen(true)
  }

  const handleCloseViewer = () => {
    setIsViewerOpen(false)
    setSelectedDocumentIndex(null)
  }

  const handleNavigateDocument = (direction: 'prev' | 'next') => {
    if (selectedDocumentIndex === null) return

    if (direction === 'prev' && selectedDocumentIndex > 0) {
      setSelectedDocumentIndex(selectedDocumentIndex - 1)
    } else if (direction === 'next' && selectedDocumentIndex < documentItems.length - 1) {
      setSelectedDocumentIndex(selectedDocumentIndex + 1)
    }
  }

  const handleDownloadAll = async () => {
    setIsDownloadingAll(true)
    await yieldToProgressPaint()

    try {
      // Dynamic import for JSZip
      const JSZip = (await import('jszip')).default
      const zip = new JSZip()

      // Add files to zip maintaining folder structure
      documentItems.forEach(document => {
        const fullPath = document.path
        if (fullPath.includes('/')) {
          // File is in a folder - create folder structure in zip
          const pathParts = fullPath.split('/')
          const fileName = pathParts.pop()!
          let currentFolder = zip

          // Create nested folders
          pathParts.forEach(folderName => {
            currentFolder = currentFolder.folder(folderName)!
          })

          currentFolder.file(fileName, document.content)
        } else {
          // File is in root - add directly
          zip.file(document.name, document.content)
        }
      })

      // Generate and download zip
      const content = await zip.generateAsync({ type: 'blob' })
      const url = window.URL.createObjectURL(content)
      const a = window.document.createElement('a')
      a.href = url

      // Create filename with timestamp
      const timestamp =
        new Date().toISOString().replace(/[:.]/g, '-').split('T')[0] +
        '_' +
        new Date().toISOString().replace(/[:.]/g, '-').split('T')[1].split('-')[0]
      a.download = `generated-documents-${timestamp}.zip`

      window.document.body.appendChild(a)
      a.click()
      window.document.body.removeChild(a)
      window.URL.revokeObjectURL(url)

      BetterToast.success(`Downloaded ${documentItems.length} files as ZIP`)
    } catch (error) {
      console.error('Download all error:', error)
      BetterToast.error('Failed to download files')
    } finally {
      setIsDownloadingAll(false)
    }
  }

  return (
    <>
      <div
        className={`w-full max-w-[800px] mx-auto px-12 ${className}`}
        style={{ padding: '0 max(48px, 3rem)' }}
      >
        {/* Header row */}
        <div className='h-[68px] rounded-[16px] w-full bg-[rgba(0,0,0,0.04)] flex items-center justify-between p-4 mb-6'>
          <span className='text-[15px] font-semibold text-[rgba(46,31,54,0.90)] font-mono'>
            {title}
          </span>
          <div className='flex items-center gap-2'>
            {onApprove && (
              <button
                className='h-9 flex p-[8px_16px] justify-center items-center gap-2 text-base rounded-[12px] bg-[rgba(91,91,213,1)] text-white transition-colors hover:bg-[rgba(91,91,213,0.9)] active:bg-[rgba(91,91,213,0.8)]'
                onClick={onApprove}
              >
                <span className='font-normal'>Approve</span>
              </button>
            )}
            {documentItems.length > 0 && (
              <DownloadProgressButton
                className='h-9 flex p-[8px_16px] justify-center items-center gap-2 text-base rounded-[12px] border border-gray-300 bg-black text-white hover:bg-gray-800 active:bg-gray-700 disabled:cursor-wait'
                onClick={handleDownloadAll}
                disabled={isDownloadingAll}
                isLoading={isDownloadingAll}
              >
                <span className='flex items-center gap-2'>
                  <IoMdDownload size={16} />
                  <span className='font-normal'>{`Download All (${documentItems.length})`}</span>
                </span>
              </DownloadProgressButton>
            )}
          </div>
        </div>

        {/* Document List with Folders */}
        <div className='space-y-4'>
          {folderGroups.map((folderGroup, folderIndex) => {
            let globalFileIndex = 0
            // Calculate the global index offset for this folder
            for (let i = 0; i < folderIndex; i++) {
              globalFileIndex += folderGroups[i].files.length
            }

            const isRootFolder = folderGroup.folderName === 'Root'
            const isExpanded = expandedFolders.has(folderGroup.folderName)

            return (
              <div key={folderGroup.folderName} className='space-y-2'>
                {/* Folder Header - Only show for non-Root folders */}
                {!isRootFolder && (
                  <div
                    className='flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors'
                    onClick={() => toggleFolderExpansion(folderGroup.folderName)}
                  >
                    {/* Folder Icon - shows expanded/collapsed state */}
                    <div className='w-4 h-4 flex items-center justify-center flex-shrink-0'>
                      {isExpanded ? (
                        <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
                          <path
                            d='M14 4H8L6 2H2C1.45 2 1 2.45 1 3V13C1 13.55 1.45 14 2 14H14C14.55 14 15 13.55 15 13V5C15 4.45 14.55 4 14 4Z'
                            fill='currentColor'
                            className='text-black/35'
                          />
                        </svg>
                      ) : (
                        <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
                          <path
                            d='M14 4H8L6 2H2C1.45 2 1 2.45 1 3V13C1 13.55 1.45 14 2 14H14C14.55 14 15 13.55 15 13V5C15 4.45 14.55 4 14 4ZM14 13H2V5H14V13Z'
                            fill='currentColor'
                            className='text-black/35'
                          />
                        </svg>
                      )}
                    </div>

                    {/* Title and Number - Left aligned with 8px gap */}
                    <div className='flex items-center gap-2 flex-1'>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodyMd'
                        fontWeight='semibold'
                        className='text-black/70'
                      >
                        {folderGroup.folderName}
                      </Typography>

                      <div className='w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center'>
                        <Typography
                          fontFamily='geist'
                          fontSize='labelSm'
                          fontWeight='medium'
                          className='text-black/50'
                        >
                          {folderGroup.files.length}
                        </Typography>
                      </div>
                    </div>
                  </div>
                )}

                {/* Files in this folder - Indent files within folders */}
                {(isRootFolder || isExpanded) && (
                  <div className='space-y-2'>
                    {folderGroup.files.map((document, fileIndex) => {
                      const documentGlobalIndex = globalFileIndex + fileIndex
                      return (
                        <div
                          key={documentGlobalIndex}
                          onClick={() => handleDocumentClick(documentGlobalIndex)}
                          className={`group flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors duration-200 ${!isRootFolder ? 'ml-6' : ''}`}
                          role='button'
                          tabIndex={0}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              handleDocumentClick(documentGlobalIndex)
                            }
                          }}
                        >
                          <div className='flex items-center gap-3 flex-1 min-w-0'>
                            {/* File icon */}
                            <div className='flex items-center justify-center flex-shrink-0'>
                              <svg
                                width='16'
                                height='16'
                                viewBox='0 0 16 16'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                              >
                                <path
                                  d='M13.3538 5.14625L9.85375 1.64625C9.80728 1.59983 9.75212 1.56303 9.69143 1.53793C9.63073 1.51284 9.56568 1.49995 9.5 1.5H3.5C3.23478 1.5 2.98043 1.60536 2.79289 1.79289C2.60536 1.98043 2.5 2.23478 2.5 2.5V13.5C2.5 13.7652 2.60536 14.0196 2.79289 14.2071C2.98043 14.3946 3.23478 14.5 3.5 14.5H12.5C12.7652 14.5 13.0196 14.3946 13.2071 14.2071C13.3946 14.0196 13.5 13.7652 13.5 13.5V5.5C13.5001 5.43432 13.4872 5.36927 13.4621 5.30858C13.437 5.24788 13.4002 5.19272 13.3538 5.14625ZM10 3.20688L11.7931 5H10V3.20688ZM12.5 13.5H3.5V2.5H9V5.5C9 5.63261 9.05268 5.75979 9.14645 5.85355C9.24021 5.94732 9.36739 6 9.5 6H12.5V13.5Z'
                                  fill='currentColor'
                                  fillOpacity='0.65'
                                />
                              </svg>
                            </div>

                            <div
                              className='overflow-hidden text-ellipsis whitespace-nowrap'
                              style={{
                                color: 'rgba(0, 0, 0, 0.88)',
                                fontFamily: 'Geist',
                                fontSize: '14px',
                                fontStyle: 'normal',
                                fontWeight: '500',
                                lineHeight: '22px'
                              }}
                            >
                              {document.name}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Document Viewer Modal */}
      {selectedDocumentIndex !== null && (
        <DocumentViewer
          isOpen={isViewerOpen}
          onClose={handleCloseViewer}
          document={documentItems[selectedDocumentIndex]}
          currentIndex={selectedDocumentIndex}
          totalDocuments={documentItems.length}
          onNavigate={handleNavigateDocument}
        />
      )}
    </>
  )
}
